import { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { createDataObjectRequest, setAiHighlightedDataObjects } from "store/dataObjects/actions";
import { fetchDataObjectByUuid } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import { buildCreateDataObjectPayload } from "./payload";
import { verifyDataObjectValues } from "./verify";
import type { ToolPartProps } from "./types";

interface AddDataObjectInput {
    values: Record<string, unknown>;
    description: string;
}

interface AddDataObjectOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    dataObjectId?: string;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "ready" | "denied" | "applying" | "applied" | "error";

export default function AddDataObjectTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<AddDataObjectInput, AddDataObjectOutput>) {
    const { dispatch, addToolOutput, tenantId, businessAreaId, schemaId, dataSet, mode, permission, schemaFields, createDataObjectResult } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    const previousDataObjectsRef = useRef<Set<string>>(new Set());
    // Snapshot of createDataObjectResult at dispatch; ignore stale prior results.
    const resultAtDispatchRef = useRef<unknown>(null);

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;
        previousDataObjectsRef.current = new Set(helpers.dataObjects.map((d) => d.dataObjectId));
        if (mode === "view" || !permission?.canAdd) {
            setPhase("denied");
            setDenyReason("You don't have permission to add records to this data set.");
            return;
        }
        if (!dataSet?.dataSetId || !schemaId || !businessAreaId) {
            setPhase("denied");
            setDenyReason("Data set context is incomplete.");
            return;
        }
    }, [state, mode, permission, dataSet, schemaId, businessAreaId, helpers.dataObjects]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied") {
            resolvedRef.current = true;
            addToolOutput({ tool: "addDataObject", toolCallId, output: { success: false, error: denyReason || "Cannot add record." } });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "applying") return;
        if (!createDataObjectResult) return;
        if (createDataObjectResult === resultAtDispatchRef.current) return;
        if (createDataObjectResult.success === true) {
            (async () => {
                // Find newly added dataObjectId by diffing the current list against the snapshot.
                const current = helpers.dataObjects.map((d) => d.dataObjectId);
                const newId = current.find((id) => !previousDataObjectsRef.current.has(id));
                if (newId) dispatch(setAiHighlightedDataObjects([newId]));

                // Verify proposed values landed on the new record.
                let report = { verified: true, appliedChanges: [] as string[], unappliedChanges: [] as string[] };
                if (!newId) {
                    // The create's POST already returned success. We just can't
                    // locate the new row in the loaded list to highlight/verify
                    // (e.g. it landed on another page). Trust the successful
                    // write rather than raising a false "not applied" alarm.
                    report = {
                        verified: true,
                        appliedChanges: Object.keys(input?.values || {}),
                        unappliedChanges: [],
                    };
                } else {
                    let refreshed: Record<string, unknown> | null = null;
                    let fetchErr: string | null = null;
                    try {
                        refreshed = await fetchDataObjectByUuid(
                            { tenantId: tenantId || "", businessAreaId: businessAreaId || "", schemaId: schemaId || "", dataSetId: dataSet?.dataSetId || "" },
                            newId,
                        );
                    } catch (e: any) {
                        fetchErr = e?.message || "fetch error";
                    }
                    if (fetchErr) {
                        // The create's POST already returned success. The
                        // single-object GET used only for verification is
                        // unreliable and can 500 server-side, so trust the
                        // successful write rather than raising a false alarm.
                        report = {
                            verified: true,
                            appliedChanges: Object.keys(input?.values || {}),
                            unappliedChanges: [],
                        };
                    } else {
                        report = verifyDataObjectValues(input?.values || {}, refreshed, schemaFields);
                    }
                }
                setPhase("applied");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "addDataObject",
                        toolCallId,
                        output: {
                            success: true,
                            dataObjectId: newId,
                            verified: report.verified,
                            appliedChanges: report.appliedChanges,
                            unappliedChanges: report.unappliedChanges,
                        },
                    });
                }
            })();
        } else if (createDataObjectResult.success === false) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "addDataObject",
                    toolCallId,
                    output: buildToolError(createDataObjectResult, "Add failed."),
                });
            }
        }
    }, [createDataObjectResult, phase, helpers.dataObjects, dispatch, toolCallId, addToolOutput]);

    const handleAdd = () => {
        if (!businessAreaId || !schemaId || !dataSet?.dataSetId) return;
        const { payload, unknownFieldNames } = buildCreateDataObjectPayload(input?.values || {}, schemaFields, {
            businessAreaId,
            schemaId,
            dataSetId: dataSet.dataSetId,
        });
        if (unknownFieldNames.length > 0 && Object.keys(payload).length === 3) {
            // Only metadata keys present — no actual values mapped. Refuse.
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "addDataObject",
                    toolCallId,
                    output: { success: false, error: `Unknown field name(s): ${unknownFieldNames.join(", ")}` },
                });
            }
            return;
        }
        resultAtDispatchRef.current = createDataObjectResult;
        setPhase("applying");
        dispatch(createDataObjectRequest(businessAreaId, schemaId, dataSet.dataSetId, payload));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "addDataObject", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    const valueEntries = Object.entries(input?.values || {});

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <PlusOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Add data object</span>
            </div>
            {input?.description && <div className="text-xs text-zinc-600">{input.description}</div>}

            {phase === "denied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && valueEntries.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-md border border-zinc-200">
                    <table className="w-full border-collapse text-left text-xs">
                        <tbody>
                            {valueEntries.map(([k, v]) => (
                                <tr key={k} className="border-t border-zinc-200 first:border-t-0">
                                    <td className="px-2 py-1 font-medium text-zinc-700">{k}</td>
                                    <td className="px-2 py-1 text-zinc-900">{v === null ? "(null)" : String(v)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {phase === "applied" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Added.
                </div>
            )}
            {phase === "applied" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> Some values were not applied
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Add failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleAdd}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C]"
                    >
                        Add
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Adding...
                </div>
            )}
        </div>
    );
}
